import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SeriesPoint } from "@mosaic/shared";
import { api, type FinancialsSide } from "../api/client.js";
import { formatXbrlValue } from "../lib/format.js";

/**
 * The reported history: the half of a spin-off thesis that is not in the filing.
 *
 * A Form 10 carries a few years of carve-out financials, so a long performance history is
 * in the company's XBRL facts, not in any document. See routes/financials.ts on the
 * server.
 *
 * It never shows a parent's figure where SpinCo's belongs: companyfacts is dimensionless,
 * so a parent's revenue includes the business being separated. And it never shows only
 * the newest value for a period: a restatement is what the owner is looking for.
 */

const CONCEPTS: Array<{ key: string; label: string }> = [
  { key: "revenue", label: "Revenue" },
  { key: "operating_income", label: "Operating income" },
  { key: "net_income", label: "Net income" },
  { key: "operating_cash_flow", label: "Operating cash flow" },
  { key: "capex", label: "Capex" },
  { key: "assets", label: "Assets" },
  { key: "long_term_debt", label: "Long-term debt" },
  { key: "equity", label: "Equity" },
];

/** Instant concepts have no annual duration; asking for one returns nothing. */
const INSTANT_CONCEPTS = new Set(["assets", "long_term_debt", "equity"]);

/**
 * The parent's and SpinCo's reported XBRL history for one concept over a chosen window of
 * years, with a refresh per company.
 */
export function Financials({ situationId }: { situationId: string }) {
  const [concept, setConcept] = useState("revenue");
  const [years, setYears] = useState(10);
  const queryClient = useQueryClient();

  const basis = INSTANT_CONCEPTS.has(concept) ? "instant" : "annual";
  const financials = useQuery({
    queryKey: ["financials", situationId, concept, basis, years],
    queryFn: () => api.situationFinancials(situationId, concept, basis, years),
    /**
     * A company nobody has asked about before is fetched on demand, so the first view can
     * answer "fetching"; poll until the worker lands it.
     */
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && (data.parent.status === "fetching" || data.spinco.status === "fetching")
        ? 3_000
        : false;
    },
  });

  const refresh = useMutation({
    mutationFn: (cik: string) => api.refreshFinancials(cik),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["financials", situationId] }),
  });

  if (financials.isError) {
    return (
      <>
        <div className="sechd">Reported history</div>
        <p className="deck">That history could not be loaded.</p>
      </>
    );
  }

  const data = financials.data;

  return (
    <>
      <div className="sechd">Reported history</div>

      <div className="fin-ctl">
        <label>
          <span className="k">Concept</span>
          <select value={concept} onChange={(e) => setConcept(e.target.value)} aria-label="Concept">
            {CONCEPTS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="k">Window</span>
          <select
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            aria-label="Window"
          >
            {[5, 10, 15, 20].map((y) => (
              <option key={y} value={y}>
                {y} years
              </option>
            ))}
          </select>
        </label>
      </div>

      {!data ? (
        <p className="deck">Loading…</p>
      ) : (
        <>
          <Side
            role="Parent"
            side={data.parent}
            onRefresh={(cik) => refresh.mutate(cik)}
            refreshing={refresh.isPending}
          />
          <Side
            role="SpinCo"
            side={data.spinco}
            onRefresh={(cik) => refresh.mutate(cik)}
            refreshing={refresh.isPending}
          />
          {/*
            The bound on everything above, stated where it is read: a parent's revenue is
            the whole group, the business being separated included.
          */}
          <p className="fin-caveat">{data.limitation}</p>
        </>
      )}
    </>
  );
}

/**
 * One company's reported series as a table and summary, or the reason there is none.
 *
 * @internal
 * @param role The label for this side, such as `Parent`.
 * @param onRefresh Called with the company's CIK to refetch its history.
 * @param refreshing Disables the refresh control while a refetch is in flight.
 */
function Side({
  role,
  side,
  onRefresh,
  refreshing,
}: {
  role: string;
  side: FinancialsSide;
  onRefresh: (cik: string) => void;
  refreshing: boolean;
}) {
  const points = side.series?.points ?? [];

  return (
    <div className="fin-side">
      <div className="fin-hd">
        <span className="fin-role">{role}</span>
        <span className="fin-name">{side.entityName ?? side.name ?? "unknown"}</span>
        {/*
          The tag that produced these numbers: "Revenue" is several XBRL tags, and a
          figure whose tag is not stated has no provenance.
        */}
        {side.resolved && <span className="fin-tag">{side.resolved.concept}</span>}
        {side.cik && side.status !== "fetching" && (
          <button className="linkbtn" onClick={() => onRefresh(side.cik!)} disabled={refreshing}>
            refresh
          </button>
        )}
      </div>

      {side.status !== "ready" || points.length === 0 ? (
        <p className="fin-none">
          {side.status === "fetching"
            ? "Fetching this company's XBRL history from EDGAR. One request, no cost — this takes a moment."
            : (side.unavailable ??
              "Nothing reported for this concept in the selected window.")}
        </p>
      ) : (
        <>
          <div className="tablewrap">
            <table className="dense">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="num">Reported</th>
                  <th className="num">As first reported</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <Row key={`${p.periodStart ?? ""}-${p.periodEnd}`} point={p} unit={side.series!.unit} />
                ))}
              </tbody>
            </table>
          </div>

          {side.summary && (
            <p className="fin-sum">
              mean <b>{formatXbrlValue(side.summary.mean, side.series!.unit)}</b> over{" "}
              {side.summary.periods} period{side.summary.periods === 1 ? "" : "s"} · range{" "}
              {formatXbrlValue(side.summary.min, side.series!.unit)} –{" "}
              {formatXbrlValue(side.summary.max, side.series!.unit)}
              {side.summary.restated > 0 && (
                <>
                  {" · "}
                  <b className="fin-restated">
                    {side.summary.restated} later restated
                  </b>
                </>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * One reported period: the latest value, the first-reported value when it was restated,
 * and the filing it came from.
 *
 * @internal
 * @param unit The series' XBRL unit, used to format both values.
 */
function Row({ point, unit }: { point: SeriesPoint; unit: string }) {
  const restated = point.wasRestated;
  return (
    <tr>
      <td>{point.periodEnd}</td>
      <td className="num">{formatXbrlValue(point.latest.value, unit)}</td>
      {/*
        Both vintages, side by side, when they differ: showing only the newer figure
        hides the restatement.
      */}
      <td className={`num${restated ? " fin-restated" : ""}`}>
        {restated ? formatXbrlValue(point.asFirstReported.value, unit) : "—"}
      </td>
      <td>
        {point.latest.form} · {point.latest.filedOn}
      </td>
    </tr>
  );
}
